import React, { useState, useEffect } from 'react';
import { Users, Plus, AlertCircle, Loader, ChevronDown, ChevronUp, X } from 'lucide-react';
import { getProjectCollaborators, addProjectCollaborator, removeProjectCollaborator } from '../../api';
import CollaboratorItem from './CollaboratorItem';
import AddCollaboratorModal from './AddCollaboratorModal';
import type { CollaboratorRole } from './AddCollaboratorModal';

interface Collaborator {
  id: number;
  userId: number;
  userName: string;
  userEmail: string;
  role: 'collaborator' | 'viewer';
  addedAt: string;
}

interface CollaboratorsListProps {
  projectId: number;
  isOwner: boolean;
  onError?: (message: string) => void;
  variant?: 'panel' | 'dropdown';
}

const CollaboratorsList: React.FC<CollaboratorsListProps> = ({ projectId, isOwner, onError, variant = 'panel' }) => {
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isRemoving, setIsRemoving] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<Collaborator | null>(null);

  useEffect(() => {
    fetchCollaborators();
  }, [projectId]);

  const fetchCollaborators = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getProjectCollaborators(projectId);
      setCollaborators(response.data || []);
    } catch (err: any) {

      const status = err.response?.status;
      const errorMsg = status === 404
        ? 'This project is no longer available. Please switch to an active project from the project menu.'
        : (err.response?.data?.error || 'Failed to load collaborators');
      setError(errorMsg);
      onError?.(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCollaborator = async (userId: number, role: CollaboratorRole = 'collaborator') => {
    try {
      setIsAdding(true);
      setError(null);
      const response = await addProjectCollaborator(projectId, userId, role);

      // Add the new collaborator to the list
      if (response.data) {
        setCollaborators(prev => [...prev, response.data]);
      }

      setIsModalOpen(false);
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to add collaborator';
      setError(errorMsg);
      onError?.(errorMsg);
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveCollaborator = (collaboratorId: number) => {
    const collaborator = collaborators.find(c => c.id === collaboratorId);
    if (!collaborator) return;
    setRemoveTarget(collaborator);
  };

  const confirmRemoveCollaborator = async () => {
    const collaborator = removeTarget;
    if (!collaborator) return;
    try {
      setIsRemoving(collaborator.id);
      setError(null);
      await removeProjectCollaborator(projectId, collaborator.userId);

      // Remove from list
      setCollaborators(prev => prev.filter(c => c.id !== collaborator.id));
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || 'Failed to remove collaborator';
      setError(errorMsg);
      onError?.(errorMsg);
    } finally {
      setIsRemoving(null);
      setRemoveTarget(null);
    }
  };

  const listBody = (
    <>
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader className="w-5 h-5 text-blue-600 animate-spin" />
        </div>
      ) : collaborators.length === 0 ? (
        <div className="text-center py-8">
          <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No collaborators yet</p>
          {isOwner && (
            <p className="text-gray-400 text-xs mt-1">Add collaborators to work together on this project</p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {collaborators.map(collaborator => (
            <CollaboratorItem
              key={collaborator.id}
              id={collaborator.id}
              userName={collaborator.userName}
              userEmail={collaborator.userEmail}
              role={collaborator.role}
              addedAt={collaborator.addedAt}
              isOwner={!isOwner}
              onRemove={handleRemoveCollaborator}
              isRemoving={isRemoving === collaborator.id}
            />
          ))}
        </div>
      )}
    </>
  );

  const modals = (
    <>
      <AddCollaboratorModal
        projectId={projectId}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={handleAddCollaborator}
        isAdding={isAdding}
      />

      {removeTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-96 flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Remove Collaborator</h3>
              <button onClick={() => setRemoveTarget(null)} className="p-1 hover:bg-gray-100 rounded transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600">
                Remove <span className="font-medium text-gray-900">{removeTarget.userName}</span> as a collaborator on this project? They will lose access immediately.
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200">
              <button
                onClick={() => setRemoveTarget(null)}
                disabled={isRemoving === removeTarget.id}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveCollaborator}
                disabled={isRemoving === removeTarget.id}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isRemoving === removeTarget.id ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Removing...
                  </>
                ) : (
                  'Remove'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (variant === 'dropdown') {
    return (
      <div className="relative">
        <button
          onClick={() => setIsDropdownOpen(p => !p)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all whitespace-nowrap ${
            isDropdownOpen
              ? 'bg-blue-50 border-blue-300 text-blue-700'
              : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300 hover:text-blue-700'
          }`}
        >
          <Users className="w-4 h-4" />
          Collaborators
          {collaborators.length > 0 && (
            <span className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">{collaborators.length}</span>
          )}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
        </button>

        {isDropdownOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsDropdownOpen(false)} />
            <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900">Collaborators</h3>
                {isOwner && (
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-xs font-medium"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add
                  </button>
                )}
              </div>
              {listBody}
            </div>
          </>
        )}

        {modals}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 px-3 py-2 mb-4">
      {/* Compact header — name pills instead of a full-width band */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          <Users className="w-4 h-4 text-blue-600 flex-shrink-0" />
          {!loading && collaborators.length === 0 && (
            <span className="text-xs text-gray-400">No collaborators</span>
          )}
          {collaborators.slice(0, 4).map(c => (
            <span
              key={c.id}
              title={c.userEmail}
              className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-xs font-medium text-blue-700"
            >
              {c.userName}
            </span>
          ))}
          {collaborators.length > 4 && (
            <span className="text-xs text-gray-500">+{collaborators.length - 4} more</span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {isOwner && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center gap-1 px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-xs font-medium"
            >
              <Plus className="w-3.5 h-3.5" />
              Add
            </button>
          )}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title={isCollapsed ? 'Show details' : 'Hide details'}
          >
            {isCollapsed ? (
              <ChevronDown className="w-4 h-4 text-gray-600" />
            ) : (
              <ChevronUp className="w-4 h-4 text-gray-600" />
            )}
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          {listBody}
        </div>
      )}

      {modals}
    </div>
  );
};

export default CollaboratorsList;
